import { ClipboardList } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { roundNameAt, roundSummary, type PlanTemplateView, type TemplateRound } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Popover, usePopover } from '../../../../components/Popover';
import styles from './RoundSetup.module.css';

/**
 * "Start from a plan" (admin redesign batch 2, Q2 a; F5.29), beside the round setup's title. The list of plans opens beside
 * the button; picking one copies its rounds into the job — how each is held, and the panel with its shares. When the job's
 * rounds already have panels, it first says beside the button exactly what will be replaced (CLAUDE.md §5: never centred),
 * and the copy comes with Undo either way. The server refuses it once a candidate has reached a round, so the button is not
 * offered then.
 */

export interface PlanChange {
  readonly roundNumber: number;
  readonly before: string | null;
  readonly after: string | null;
}

/** What applying the plan does to each round, in one line each; empty when nothing set by hand would be replaced. */
export function planChanges(plan: PlanTemplateView, rounds: readonly Pick<TemplateRound, 'mode' | 'panel'>[]): readonly PlanChange[] {
  const isSetByHand = rounds.some((round) => round.panel.length > 0);
  if (!isSetByHand) return [];
  return Array.from({ length: Math.max(plan.rounds.length, rounds.length) }, (_, index) => {
    const before = rounds[index];
    const after = plan.rounds[index];
    return { roundNumber: index + 1, before: before ? roundSummary(before) : null, after: after ? roundSummary(after) : null };
  }).filter((change) => change.before !== change.after);
}

export function RoundSetupPlanPicker({
  plans,
  rounds,
  isBusy,
  onApply,
}: {
  readonly plans: readonly PlanTemplateView[];
  readonly rounds: readonly Pick<TemplateRound, 'mode' | 'panel'>[];
  readonly isBusy: boolean;
  readonly onApply: (plan: PlanTemplateView) => void;
}): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [chosen, setChosen] = useState<PlanTemplateView | null>(null);
  const firstRef = useRef<HTMLButtonElement | null>(null);

  // The panel is placed after it mounts, so focus waits a frame for it (review W4).
  useEffect(() => {
    if (!isOpen || chosen) return undefined;
    const frame = requestAnimationFrame(() => firstRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen, chosen]);

  function finish(): void {
    setChosen(null);
    close();
  }

  function pick(plan: PlanTemplateView): void {
    if (planChanges(plan, rounds).length > 0) {
      setChosen(plan);
      return;
    }
    finish();
    onApply(plan);
  }

  const changes = chosen ? planChanges(chosen, rounds) : [];

  return (
    <>
      <Button
        ref={triggerRef}
        size="sm"
        variant="outline"
        icon={ClipboardList}
        disabled={isBusy}
        aria-expanded={isOpen}
        onClick={() => {
          setChosen(null);
          toggle();
        }}
      >
        Start from a plan
      </Button>
      <Popover anchor={anchor} onClose={finish} align="right" width={360} label="Start from a plan">
        {chosen === null ? (
          <div className={styles.planMenu}>
            <p className={styles.planLead}>Copies a plan's rounds into this job: how each is held, and its panel with the shares. Editing the plan later never changes this job.</p>
            <ul className={styles.planList}>
              {plans.map((plan, index) => (
                <li key={plan.id}>
                  <button ref={index === 0 ? firstRef : undefined} type="button" className={styles.planOption} onClick={() => pick(plan)}>
                    <span className={styles.planName}>{plan.name}</span>
                    <span className={styles.planMeta}>
                      {plan.rounds.length} round{plan.rounds.length === 1 ? '' : 's'} · {plan.rounds.map(roundSummary).join(' → ')}
                    </span>
                    {plan.description && <span className={styles.planMeta}>{plan.description}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className={styles.planMenu}>
            <h3 className={styles.planTitle}>Use {chosen.name}?</h3>
            <p className={styles.planLead}>It replaces what is set on this job's rounds now:</p>
            <ul className={styles.planChanges}>
              {changes.map((change) => (
                <li key={change.roundNumber}>
                  <strong>{roundNameAt(change.roundNumber - 1)}:</strong>{' '}
                  {change.after === null ? `${change.before ?? ''} → switched off` : change.before === null ? `added, ${change.after}` : `${change.before} → ${change.after}`}
                </li>
              ))}
            </ul>
            <p className={styles.planLead}>Each round keeps its interview kit. You can undo it straight after.</p>
            <div className={styles.planActions}>
              <Button size="sm" variant="ghost" onClick={() => setChosen(null)}>
                Back
              </Button>
              <Button
                size="sm"
                variant="solid"
                autoFocus
                onClick={() => {
                  const plan = chosen;
                  finish();
                  onApply(plan);
                }}
              >
                Use this plan
              </Button>
            </div>
          </div>
        )}
      </Popover>
    </>
  );
}
